/**
 * TimelineFilterDateRange Component
 *
 * Date range picker for filtering by date range.
 */

import React, { useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "lucide-react";
import type { TimelineFilters } from "../types/timeline-filters";

interface TimelineFilterDateRangeProps {
  filters: TimelineFilters;
  onDateFromChange: (date: string | undefined) => void;
  onDateToChange: (date: string | undefined) => void;
}

const TimelineFilterDateRangeBase: React.FC<TimelineFilterDateRangeProps> = ({
  filters,
  onDateFromChange,
  onDateToChange,
}) => {
  const handleDateFromChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onDateFromChange(e.target.value || undefined);
    },
    [onDateFromChange]
  );

  const handleDateToChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onDateToChange(e.target.value || undefined);
    },
    [onDateToChange]
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Label className="text-sm font-semibold text-gray-700">
          Date Range
        </Label>
        <Calendar className="h-4 w-4 text-gray-500" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label htmlFor="dateFrom" className="text-xs text-gray-600 font-medium">
            From Date
          </Label>
          <Input
            id="dateFrom"
            type="date"
            value={filters.dateFrom || ""}
            onChange={handleDateFromChange}
            className="h-9 text-sm"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="dateTo" className="text-xs text-gray-600 font-medium">
            To Date
          </Label>
          <Input
            id="dateTo"
            type="date"
            value={filters.dateTo || ""}
            onChange={handleDateToChange}
            className="h-9 text-sm"
          />
        </div>
      </div>
    </div>
  );
};

export const TimelineFilterDateRange = React.memo(
  TimelineFilterDateRangeBase
);
